// /app/frontend/src/components/frugal-v2/components/CloseStateBanner.jsx
// Shows a yellow grace banner when previous month is pending close.

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CloseStateBanner({ token, onClose }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState(null);
  const [closing, setClosing] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/v2/frugal/closing/close-state`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setState(await res.json());
    } catch {
      /* silent */
    }
  }, [token]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!state || state.state !== 'grace') return null;

  const isEs = i18n.language?.startsWith('es');
  const monthName = state.pending_month_key
    ? new Date(state.pending_month_key + '-01T12:00:00').toLocaleDateString(
        isEs ? 'es-ES' : 'en-US',
        { month: 'long', year: 'numeric' }
      )
    : '';

  const handleClose = async () => {
    setClosing(true);
    try {
      const res = await fetch(`${API}/api/v2/frugal/closing/execute`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm_unpaid: true })
      });
      if (res.ok) {
        toast.success(isEs ? 'Mes cerrado correctamente' : 'Month closed');
        await refresh();
        onClose?.();
      } else {
        const err = await res.json();
        toast.error(err.message || err.detail || 'Error');
      }
    } catch {
      toast.error(isEs ? 'Error de conexión' : 'Connection error');
    } finally {
      setClosing(false);
    }
  };

  return (
    <div
      className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-3 flex items-center justify-between gap-3"
      data-testid="grace-close-banner"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-amber-200 font-medium">
            {isEs
              ? `Cierre pendiente de ${monthName}`
              : `Close pending for ${monthName}`}
          </div>
          <div className="text-xs text-amber-100/80 mt-0.5">
            {isEs
              ? 'Hoy puedes ajustar saldos de bancos y tarjetas. No podrás aprobar emails ni registrar gastos. Si no cierras hoy, mañana se cerrará automáticamente.'
              : 'Today you can adjust bank/card balances. Email approvals and new expenses are blocked. If you don\u2019t close today, the system will close it tomorrow.'}
          </div>
        </div>
      </div>
      <button
        onClick={handleClose}
        disabled={closing}
        data-testid="grace-close-btn"
        className="px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
      >
        {closing
          ? isEs ? 'Cerrando...' : 'Closing...'
          : isEs ? 'Cerrar mes' : 'Close month'}
      </button>
    </div>
  );
}
